import axios from 'axios';
import { Client, Account } from 'appwrite';
import { Capacitor } from '@capacitor/core';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend-7n8k.onrender.com';
const API_URL = `${BACKEND_URL}/api`;

// Initialize Appwrite SDK
const client = new Client();
client
    .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://fra.cloud.appwrite.io/v1')
    .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID || '694e577200324c720101');

const account = new Account(client);

// Configure axios to include credentials (cookies)
axios.defaults.withCredentials = true;

const apiClient = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    timeout: 60000, // Important for Render free tier cold starts
    headers: {
        'Content-Type': 'application/json'
    }
});

let cachedJwt = null;
let jwtExpiration = 0;
let jwtPromise = null; // Prevents concurrent createJWT calls

// Intercept every request before it leaves the frontend
apiClient.interceptors.request.use(
    async (config) => {
        try {
            console.log(`[Diagnostic] Intercepting request to ${config.url}`);
            const now = Date.now();
            
            // 1. Check if we have a valid cached token
            if (cachedJwt && now < jwtExpiration) {
                config.headers['Authorization'] = `Bearer ${cachedJwt}`;
                console.log(`[API Request] ${config.method.toUpperCase()} ${config.url} - Using Cached Appwrite JWT`);
                return config;
            }

            // 2. If a JWT request is already in flight, wait for it instead of starting a new one
            if (jwtPromise) {
                console.log(`[Diagnostic] JWT generation already in flight. Waiting for existing promise...`);
                const token = await jwtPromise;
                config.headers['Authorization'] = `Bearer ${token}`;
                return config;
            }

            console.log(`[Diagnostic] No valid cached JWT. Requesting new JWT from Appwrite...`);
            
            // 3. Start a new JWT generation and store the promise
            // BYPASS APPWRITE SDK BUG: We use a direct Axios call instead of account.createJWT()
            const appwriteEndpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://cloud.appwrite.io/v1';
            const appwriteProjectId = process.env.REACT_APP_APPWRITE_PROJECT_ID || 'scanbell-project';
            
            jwtPromise = axios.post(
                `${appwriteEndpoint}/account/jwt`,
                {},
                {
                    headers: {
                        'X-Appwrite-Project': appwriteProjectId,
                        'Content-Type': 'application/json',
                        'X-Fallback-Cookies': localStorage.getItem('cookieFallback') || ''
                    },
                    withCredentials: true // Crucial: Sends the Appwrite session cookie
                }
            )
                .then(axiosResponse => {
                    const jwtResponse = axiosResponse.data;
                    if (jwtResponse && jwtResponse.jwt) {
                        cachedJwt = jwtResponse.jwt;
                        jwtExpiration = Date.now() + (14 * 60 * 1000); // Cache for 14 minutes
                        console.log(`[API Request] Successfully Generated New Appwrite JWT (SDK Bypass Success!)`);
                        return cachedJwt;
                    }
                    throw new Error("Appwrite returned an empty JWT response");
                })
                .catch(error => {
                    jwtPromise = null; // Clear the promise so future requests can try again
                    throw error;
                });

            const token = await jwtPromise;
            config.headers['Authorization'] = `Bearer ${token}`;
            
        } catch (error) {
            // Changed to console.log because the user's DevTools has Warnings AND Errors hidden
            console.log(`\n================= APPWRITE ERROR =================`);
            console.log(`🚨 Appwrite failed to generate a JWT!`);
            console.log(`Error Code:`, error.code);
            console.log(`Error Message:`, error.message);
            
            if (error.code === 429 || error.message?.includes('Rate limit') || error.message?.includes('json')) {
                console.log(`\n❌ YOU HAVE HIT APPWRITE'S RATE LIMIT / SDK BUG!`);
                console.log(`Appwrite has locked this account from generating JWTs for 60 minutes.`);
                console.log(`Forcibly wiping your browser's local storage to break the cached session trap...`);
                localStorage.clear();
                sessionStorage.clear();
                console.log(`Local storage cleared! Please Hard Refresh the page and log in with a DIFFERENT email address!`);
            }
            console.log(`==================================================\n`);
            
            console.log(`[API Request] ${config.method.toUpperCase()} ${config.url} - Proceeding without token because JWT generation failed.`);
        }
        return config;
    },
    (error) => Promise.reject(error)
);

export const appwriteService = {
    // Authentication methods (OAuth)
    loginWithGoogle: async () => {
        const successUrl = Capacitor.isNativePlatform() 
            ? 'appwrite-callback-694e577200324c720101://com.scanbell.app/success' 
            : window.location.origin + '/dashboard';
            
        const failureUrl = Capacitor.isNativePlatform() 
            ? 'appwrite-callback-694e577200324c720101://com.scanbell.app/failure' 
            : window.location.origin + '/login';

        if (Capacitor.isNativePlatform()) {
            try {
                const { Browser } = await import('@capacitor/browser');
                
                // createOAuth2Token returns a Promise<string> in newer SDKs
                let url;
                if (typeof account.createOAuth2Token === 'function') {
                    try {
                        url = await account.createOAuth2Token('google', successUrl, failureUrl, ['profile', 'email']);
                    } catch (e) {
                        console.error("Failed to create OAuth2 token", e);
                    }
                }
                
                // Fallback to manual URL construction if it doesn't return a URL
                if (!url || typeof url !== 'string') {
                    const endpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://fra.cloud.appwrite.io/v1';
                    const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID || '694e577200324c720101';
                    url = `${endpoint}/account/sessions/oauth2/google?project=${projectId}&success=${encodeURIComponent(successUrl)}&failure=${encodeURIComponent(failureUrl)}&scopes=${encodeURIComponent('profile email')}`;
                }
                
                await Browser.open({ url: url.toString() });
                return;
            } catch (e) {
                console.error("Failed to open native browser for OAuth", e);
            }
        }

        return account.createOAuth2Session(
            'google',
            successUrl,
            failureUrl,
            ['profile', 'email']
        );
    },

    completeOAuthSession: async (userId, secret) => {
        try {
            await account.createSession(userId, secret);
            return true;
        } catch (error) {
            console.error("Failed to complete native OAuth session", error);
            return false;
        }
    },

    syncOAuthSession: async () => {
        try {
            console.log("appwriteService: Checking for Appwrite session...");
            const appwriteUser = await account.get();
            if (appwriteUser) {
                console.log("appwriteService: Appwrite session found for:", appwriteUser.email);
                console.log("appwriteService: User ID:", appwriteUser.$id);

                // Get the current session instead of creating a JWT
                console.log("appwriteService: Getting session details...");
                const session = await account.getSession('current');
                console.log("appwriteService: Session retrieved successfully");

                console.log("appwriteService: Synchronizing with backend...");
                const response = await apiClient.post('/auth/oauth-sync', {
                    userId: appwriteUser.$id,
                    email: appwriteUser.email,
                    name: appwriteUser.name
                });

                console.log("appwriteService: Backend sync complete. User data:", response.data);
                return response.data.user || response.data;
            }
            console.log("appwriteService: No Appwrite session found.");
            return null;
        } catch (error) {
            console.log('appwriteService: syncOAuthSession error:', error.message || error);
            if (error.response) {
                console.log('appwriteService: Backend response:', error.response.status, error.response.data);
            } else if (error.request) {
                console.log('appwriteService: No response from backend');
            } else {
                console.log('appwriteService: Error details:', error.message);
            }
            console.log('🚨 This means account.get() failed! The user is not actually authenticated in Appwrite.');
            return null;
        }
    },

    getCurrentUser: async () => {
        try {
            const response = await apiClient.get('/auth/me');
            const user = response.data;
            if (user && !user.$id && user.id) {
                user.$id = user.id; // Map for compatibility with frontend code expecting $id
            }
            return user;
        } catch (error) {
            // 401 means not logged in
            if (error.response && error.response.status === 401) {
                return null;
            }
            console.error('Error getting current user:', error);
            return null;
        }
    },

    logout: async () => {
        try {
            // Delete Appwrite session
            try {
                await account.deleteSession('current');
            } catch (e) {
                console.warn("Appwrite session already gone or failed to delete:", e);
            }
        } catch (error) {
            console.error('Error logging out:', error);
            throw error;
        }
    },

    // Database methods
    getUserSettings: async (userId) => {
        try {
            // Parallel fetch
            const [doorbellResponse, qrResponse] = await Promise.all([
                apiClient.get('/doorbell/settings'),
                apiClient.get('/qr/settings')
            ]);

            return {
                doorbell: doorbellResponse.data,
                qr: qrResponse.data
            };
        } catch (error) {
            console.error('Error getting user settings:', error);
            if (error.response) {
                console.error('Response error:', error.response.status, error.response.data);
            } else if (error.request) {
                console.error('No response received from backend');
            } else {
                console.error('Error details:', error.message);
            }
            return { doorbell: null, qr: null };
        }
    },

    updateDoorbellSettings: async (userId, updates) => {
        try {
            const response = await apiClient.put('/doorbell/settings', updates);
            return response.data;
        } catch (error) {
            console.error('Error updating doorbell settings:', error);
            throw error;
        }
    },

    updateQRSettings: async (userId, updates) => {
        try {
            const response = await apiClient.put('/qr/settings', updates);
            return response.data;
        } catch (error) {
            console.error('Error updating QR settings:', error);
            throw error;
        }
    },

    getCallHistory: async (userId) => {
        try {
            const response = await apiClient.get('/calls/history');
            return response.data;
        } catch (error) {
            console.error('Error getting call history:', error);
            return [];
        }
    },

    getCallStats: async (userId) => {
        try {
            const response = await apiClient.get('/calls/stats');
            return response.data;
        } catch (error) {
            console.error('Error getting call stats:', error);
            return { total: 0, answered: 0, missed: 0, blocked: 0 };
        }
    },

    registerFCMToken: async (userId, token, platform = null) => {
        try {
            const response = await apiClient.post('/notifications/register-token', {
                token,
                platform
            });
            return response.data;
        } catch (error) {
            console.error('Error registering FCM token:', error);
            throw error;
        }
    },

    getNotificationStatus: async () => {
        try {
            const response = await apiClient.get('/notifications/status');
            return response.data;
        } catch (error) {
            console.error('Error getting notification status:', error);
            throw error;
        }
    },

    testNotification: async () => {
        try {
            const response = await apiClient.post('/notifications/test');
            return response.data;
        } catch (error) {
            console.error('Error testing notification:', error);
            throw error;
        }
    },

    regenerateLink: async (userId) => {
        try {
            const response = await apiClient.post('/doorbell/regenerate-link');
            return response.data;
        } catch (error) {
            console.error('Error regenerating link:', error);
            throw error;
        }
    },

    requestUpgrade: async () => {
        try {
            const response = await apiClient.post('/subscription/request-upgrade');
            return response.data;
        } catch (error) {
            console.error('Error requesting upgrade:', error);
            throw error;
        }
    },

    contactAdmin: async (name, email, message, userId = null) => {
        try {
            const response = await apiClient.post('/contact', {
                name,
                email,
                message,
                user_id: userId
            });
            return response.data;
        } catch (error) {
            console.error('Error contacting admin:', error);
            throw error;
        }
    },

    deleteCall: async (callId) => {
        try {
            const response = await apiClient.delete(`/calls/${callId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting call:', error);
            throw error;
        }
    },

    blockIP: async (userId, ip) => {
        try {
            const response = await apiClient.post('/doorbell/block', { ip });
            return response.data;
        } catch (error) {
            console.error('Error blocking IP:', error);
            throw error;
        }
    },

    unblockIP: async (userId, ip) => {
        try {
            const response = await apiClient.post('/doorbell/unblock', { ip });
            return response.data;
        } catch (error) {
            console.error('Error unblocking IP:', error);
            throw error;
        }
    },

    // Public / Visitor methods
    getDoorbellInfo: async (userId) => {
        try {
            const response = await apiClient.get(`/call/info/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error getting doorbell info:', error);
            throw error;
        }
    },

    getVisitorIP: async () => {
        return '0.0.0.0';
    },

    createCallHistory: async (callData) => {
        // 'callData' contains { user_id, visitor_name, room_id, status='ringing', ... }
        // We map this to a "ring" signaling message.
        const messageData = {
            room_id: callData.room_id || callData.user_id,
            sender_type: 'visitor',
            message_type: 'ring',
            payload: {
                visitor_name: callData.visitor_name,
                visitor_ip: callData.visitor_ip
            }
        };

        try {
            console.log("Sending ring signal with data:", messageData);
            const response = await apiClient.post('/signaling/send', messageData);
            console.log("Ring signal response:", response.data);

            // The modified backend now returns { message_id, call_id }
            // Expected return conforms to Appwrite document structure with $id
            return {
                $id: response.data.call_id || 'pending',
                ...callData
            };
        } catch (error) {
            console.error("Error sending ring signal:", error);
            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);
            }
            throw error;
        }
    },

    updateCallDuration: async (callId, duration) => {
        try {
            const response = await apiClient.post('/signaling/end-call', {
                call_id: callId,
                duration_seconds: duration
            });
            return response.data;
        } catch (error) {
            console.error('Error updating call duration:', error);
        }
    },

    // WebRTC Signaling
    sendSignalingMessage: async (messageData) => {
        try {
            const response = await apiClient.post('/signaling/send', messageData);
            return response.data;
        } catch (error) {
            console.error('Error sending signaling message:', error);
            throw error;
        }
    },

    pollSignalingMessages: async (roomId, senderType) => {
        try {
            const response = await apiClient.get(`/signaling/poll/${roomId}`, {
                params: { sender_type: senderType }
            });
            
            // Log detailed JSON payload of every message
            if (response.data && response.data.messages && response.data.messages.length > 0) {
                console.log(`[Signaling Poll] Retrieved ${response.data.messages.length} messages:`);
                response.data.messages.forEach(msg => {
                    console.log(`- Message ID: ${msg.$id || 'unknown'} | Type: ${msg.message_type}`);
                    console.log(`  Payload: ${JSON.stringify(msg.payload)}`);
                });
            }
            
            return response.data;
        } catch (error) {
            console.error('Error polling signaling messages:', error);
            return { messages: [] };
        }
    }
};

export const verifyAppwriteSetup = async () => {
    try {
        await apiClient.get('/health');
        console.log("Backend connection verified");
        return { success: true };
    } catch (e) {
        console.log("Backend connection failed", e);
        return { success: false };
    }
};

export default appwriteService;